import { type ChangeEvent, useLayoutEffect, useMemo, useRef } from "react";
import { create } from "zustand";
import type { SaverName } from "./pendingChangesSavers";
import {
  type CacheUpdaterName,
  type PostProcessorName,
  SERIALIZERS,
  type SerializerKind,
} from "./pendingChangesSerializers";

/**
 * # Pending-changes store
 *
 * A root-level "cart of unsaved edits" for the application currently being
 * viewed. Every editable `Modifiable*` field stages into this store; the
 * `PendingChangesSaveBar` mounted at `AppRoot` flushes all edits in one
 * mutation on Save and discards them on Cancel. Replaces per-field
 * `useApplicationMutations` + `useApplicationAuthed` + debouncer — the
 * 423ms passive-effect spike the profile surfaced on PreFundedDashboard.
 *
 * ## Single-application scope
 *
 * The store is a singleton bound to ONE applicationId at a time via
 * `useBindApplicationId(id)`. One screen binds one app; on unmount/id-change
 * the binding clears. Screens that need the batched-save UX call
 * `useBindApplicationId` near their root (see `ApplicationOverview.tsx`).
 *
 * ## Opting in / opting out
 *
 * Default: a controlled + editable `Modifiable*` field routes through this
 * store. No implicit checks or hook subscriptions in the field itself.
 *
 * A field falls back to the legacy per-field mutation editor when any of:
 *
 *   - `props.manualSave` — caller owns saving via `onManualChange`; store
 *     is irrelevant.
 *   - `props.immediateSave` — explicit opt-out for callers that want
 *     today's "save on blur" semantics (e.g. multi-app list/row editing
 *     contexts where binding a single applicationId doesn't fit).
 *   - Inline `mutateValueBeforeUpdate` / `cacheUpdateFnProperty` /
 *     `updateOtherApplicationValuesOnUpdate` closures — these haven't been
 *     migrated to the named `postProcess` / `cacheUpdate` kinds yet, so
 *     we keep their exact legacy semantics until they are.
 *
 * If none of the above apply, the field stages into this store and the
 * save bar flushes on Save / discards on Cancel. If no screen has bound an
 * applicationId via `useBindApplicationId`, the save bar shows an explicit
 * error toast — better than silent failure or cross-app writes.
 *
 * Modal screens that show one app at a time (e.g. pipeline deal-card
 * modal, LOS checklist modal) should call `useBindApplicationId(app.id)`
 * inside the modal so their fields get batched saves automatically.
 *
 * ## Serializable entries
 *
 * A pending change is plain, serializable data — no function closures. Any
 * transformation logic is routed via string keys into the registries in
 * `pendingChangesSerializers.ts`. This keeps the store inspectable (devtools,
 * logging), GC-friendly, and ready for future features like persistence,
 * undo, or draft-restore across reloads.
 */
export type PendingEntry = {
  path: string;
  /** Raw value from the input element (string for numeric/date masks, etc.). */
  rawValue: any;
  /** How to convert rawValue → display string, and rawValue → storage value. */
  serializer: SerializerKind;
  /**
   * Optional named post-processor applied at save time AFTER the serializer's
   * toStorage. Used for custom shapes that the default saver still needs to
   * package into the property/application PATCH body.
   */
  postProcess?: { name: PostProcessorName; args?: Record<string, any> };
  /**
   * Optional named cache-updater composed into the optimistic property cache
   * update applied by the default saver.
   */
  cacheUpdate?: { name: CacheUpdaterName; args?: Record<string, any> };
  /**
   * Optional named saver. Defaults to `applicationFields` (today's batched
   * PATCH path). Use a custom saver when the field's write target isn't a
   * column on application/property/loan — e.g. a child entity row whose
   * own endpoint is the right place to land the value.
   */
  saver?: { name: SaverName; args?: Record<string, any> };
  /** Review context for edits staged by the local agent. Never affects saving. */
  proposal?: {
    source: "localAgent";
    reason: string;
    evidenceDocumentIds?: string[];
    evidence?: Array<{
      documentId: string;
      pages?: number[];
      excerpt?: string;
      method?: "text" | "ocr" | "visual";
      confidence?: number;
    }>;
  };
};

/**
 * Where a save is in its lifecycle.
 *
 * `saving` covers the writes *and* the refetch they invalidate — the second half
 * is most of the wall-clock, and without it the bar would disappear while the
 * app was still visibly settling. `saved` is the brief acknowledgement that
 * follows, so a save that changes nothing on screen still reads as having
 * happened.
 */
export type PendingSaveState = "idle" | "saving" | "saved";

interface ApplicationPendingChangesStore {
  applicationId?: string;
  changes: Record<string, PendingEntry>;
  saveState: PendingSaveState;
  /** How many entries the in-flight save carried — `changes` is cleared as it starts. */
  savingCount: number;
  setApplicationId: (id?: string) => void;
  setSaveState: (state: PendingSaveState, count?: number) => void;
  stage: (entry: PendingEntry) => void;
  discard: (path: string) => void;
  discardAll: () => void;
}

export const useApplicationPendingChangesStore = create<ApplicationPendingChangesStore>((set) => ({
  applicationId: undefined,
  changes: {},
  saveState: "idle",
  savingCount: 0,
  setApplicationId: (id) =>
    set((s) => {
      if (s.applicationId === id) return s;
      // A save in flight belongs to the application being left behind; leaving
      // the flag set would show a "Saving…" bar over the next file.
      return { applicationId: id, changes: {}, saveState: "idle", savingCount: 0 };
    }),
  setSaveState: (state, count) =>
    set((s) => ({ saveState: state, savingCount: count ?? (state === "idle" ? 0 : s.savingCount) })),
  stage: (entry) =>
    set((s) => ({
      changes: { ...s.changes, [entry.path]: entry },
    })),
  discard: (path) =>
    set((s) => {
      if (!(path in s.changes)) return s;
      const { [path]: _omit, ...rest } = s.changes;
      return { changes: rest };
    }),
  discardAll: () => set({ changes: {} }),
}));

export type PendingApplicationEntityRowKind =
  | "applicant"
  | "property"
  | "income"
  | "debt"
  | "expense"
  | "asset";

/**
 * Remove staged child-row edits after those rows have been successfully
 * deleted or merged. Otherwise the global Save action can later PATCH a
 * deleted row or a hidden merge source.
 */
export const discardPendingApplicationEntityRows = (
  kind: PendingApplicationEntityRowKind,
  entityIds: string[]
) => {
  if (!entityIds.length) {
    return;
  }

  const ids = new Set(entityIds);
  const store = useApplicationPendingChangesStore.getState();
  for (const entry of Object.values(store.changes)) {
    if (entry.saver?.name !== "applicationEntityRow") {
      continue;
    }
    const args = entry.saver.args as { kind?: PendingApplicationEntityRowKind; id?: string } | undefined;
    if (args?.kind === kind && args.id && ids.has(args.id)) {
      store.discard(entry.path);
    }
  }
};

/** Drop staged edits for an insurance policy whose row was deleted, so Save can't PATCH it. */
export const discardPendingInsurancePolicy = (policyId: string) => {
  const store = useApplicationPendingChangesStore.getState();
  for (const entry of Object.values(store.changes)) {
    if (entry.saver?.name === "insurancePolicy" && entry.saver.args?.policyId === policyId) {
      store.discard(entry.path);
    }
  }
};

export const useIsPathDirty = (path: string) => useApplicationPendingChangesStore((s) => path in s.changes);

export const usePendingChangeRaw = (path: string) =>
  useApplicationPendingChangesStore((s) => (path in s.changes ? s.changes[path].rawValue : undefined));

export const usePendingCount = () => useApplicationPendingChangesStore((s) => Object.keys(s.changes).length);

export const useHasAnyPending = () => usePendingCount() > 0;

export const usePendingApplicationId = () => useApplicationPendingChangesStore((s) => s.applicationId);

export const usePendingSaveState = () => useApplicationPendingChangesStore((s) => s.saveState);

export const usePendingSavingCount = () => useApplicationPendingChangesStore((s) => s.savingCount);

/**
 * The shape a form field needs to bind itself to the pending-changes store.
 * Spread the return value straight onto a stock <input>/<Input>/<Select>:
 *   const bind = usePendingField({ path, controlledValue, serializer: "currency" });
 *   <Input {...inputProps} {...bind} />
 */
export type PendingFieldBinding = {
  value: string;
  onChange: (evt: ChangeEvent<HTMLInputElement>) => void;
  /** True when the user has an unsaved edit staged for this path. */
  isDirty: boolean;
};

export type UsePendingFieldArgs = {
  path: string;
  controlledValue: any;
  serializer: SerializerKind;
  /** Optional override for the server-value display string. Equality check against onChange input uses this. */
  controlledDisplayValue?: string;
  /** Optional save-time post-processor (named, so store entries stay serializable). */
  postProcess?: { name: PostProcessorName; args?: Record<string, any> };
  /** Optional optimistic cache updater (named). */
  cacheUpdate?: { name: CacheUpdaterName; args?: Record<string, any> };
  /** Optional named saver — defaults to the batched-PATCH path when unset. */
  saver?: { name: SaverName; args?: Record<string, any> };
};

/**
 * Core hook for any field that wants to stage edits into the root-level store
 * instead of firing its own per-field mutation. Owns exactly one store
 * subscription (to this path). The returned onChange is a stable reference —
 * callers don't need to memoize the args.
 */
export const usePendingField = ({
  path,
  controlledValue,
  serializer,
  controlledDisplayValue,
  postProcess,
  cacheUpdate,
  saver,
}: UsePendingFieldArgs): PendingFieldBinding => {
  const pendingRaw = usePendingChangeRaw(path);
  const isDirty = pendingRaw !== undefined;
  const serverDisplay = controlledDisplayValue ?? SERIALIZERS[serializer].toDisplay(controlledValue);
  const value = isDirty ? String(pendingRaw ?? "") : serverDisplay;

  const latest = useRef({ path, serverDisplay, serializer, postProcess, cacheUpdate, saver });
  latest.current = { path, serverDisplay, serializer, postProcess, cacheUpdate, saver };

  const onChange = useMemo(
    () => (evt: ChangeEvent<HTMLInputElement>) => {
      const {
        path: p,
        serverDisplay: sd,
        serializer: ser,
        postProcess: pp,
        cacheUpdate: cu,
        saver: sv,
      } = latest.current;
      const raw = evt.target.value;
      const store = useApplicationPendingChangesStore.getState();
      if (raw === sd) {
        store.discard(p);
        return;
      }
      store.stage({
        path: p,
        rawValue: raw,
        serializer: ser,
        postProcess: pp,
        cacheUpdate: cu,
        saver: sv,
      });
    },
    []
  );

  return { value, onChange, isDirty };
};

/**
 * Imperative equivalent of usePendingField — for callers that need to stage on
 * events other than a DOM change (Select's onValueChange, button clicks, etc.).
 */
export const usePendingFieldSetter = ({
  path,
  controlledValue,
  serializer,
  controlledDisplayValue,
  postProcess,
  cacheUpdate,
  saver,
}: UsePendingFieldArgs) => {
  const latest = useRef({
    path,
    controlledValue,
    serializer,
    controlledDisplayValue,
    postProcess,
    cacheUpdate,
    saver,
  });
  latest.current = {
    path,
    controlledValue,
    serializer,
    controlledDisplayValue,
    postProcess,
    cacheUpdate,
    saver,
  };

  return useMemo(
    () => (rawValue: unknown) => {
      const {
        path: p,
        controlledValue: cv,
        serializer: ser,
        controlledDisplayValue: cdv,
        postProcess: pp,
        cacheUpdate: cu,
        saver: sv,
      } = latest.current;
      const store = useApplicationPendingChangesStore.getState();
      const sd = cdv ?? SERIALIZERS[ser].toDisplay(cv);
      const asDisplay = rawValue === null || rawValue === undefined ? "" : String(rawValue);
      if (asDisplay === sd) {
        store.discard(p);
        return;
      }
      store.stage({
        path: p,
        rawValue,
        serializer: ser,
        postProcess: pp,
        cacheUpdate: cu,
        saver: sv,
      });
    },
    []
  );
};

type ActiveApplicationBinding = {
  applicationId: string;
  order: number;
};

/**
 * More than one mounted surface can edit the same application (for example the
 * persistent application sidebar and the active LOS tab). Track each hook
 * instance independently so one surface unmounting cannot clear another's
 * binding. The most recently mounted binding wins when different applications
 * overlap, and removing it restores the previous binding.
 */
const activeApplicationBindings = new Map<symbol, ActiveApplicationBinding>();
let applicationBindingOrder = 0;

const syncActiveApplicationBinding = () => {
  let active: ActiveApplicationBinding | undefined;
  for (const binding of activeApplicationBindings.values()) {
    if (!active || binding.order > active.order) {
      active = binding;
    }
  }
  useApplicationPendingChangesStore.getState().setApplicationId(active?.applicationId);
};

/**
 * Bind the currently-viewed application id to the pending-changes store.
 * On id change or unmount, restores the most recent remaining binding.
 * Call near the top of any screen that renders controlled+editable Modifiable* fields.
 */
export const useBindApplicationId = (id?: string) => {
  const owner = useRef(Symbol("application-pending-changes-binding"));

  useLayoutEffect(() => {
    // A mounted-but-closed modal or a screen waiting for its application query
    // does not own the global binding and must not clear a ready sibling.
    if (!id) {
      return;
    }

    activeApplicationBindings.set(owner.current, {
      applicationId: id,
      order: ++applicationBindingOrder,
    });
    syncActiveApplicationBinding();

    return () => {
      activeApplicationBindings.delete(owner.current);
      syncActiveApplicationBinding();
    };
  }, [id]);
};
